import { useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearConversation } from '../store/conversationSlice';
import { clearLandmineAlerts } from '../store/slices/landmineSlice';
import { clearCompetitorAlerts } from '../store/slices/competitorSlice';
import { clearSalesPlaybookSession } from '../store/slices/salesPlaybookSlice';
import { sessionManager } from '../services/sessionManager';
import { SessionSummaryData } from '../components/SessionSummaryModal';

/**
 * Hook to compute and display session summary modal.
 * Provides reusable session summary logic for both MeetBotModal and ConversationPage.
 */
export function useSessionSummary() {
  const dispatch = useAppDispatch();
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [sessionSummaryData, setSessionSummaryData] = useState<SessionSummaryData | null>(null);

  // Get data from Redux
  const transcripts = useAppSelector((state) => state.conversation.transcripts);
  const entries = useAppSelector((state) => state.conversation.entries);
  const landmineAlerts = useAppSelector((state) => state.landmine.allAlerts);
  const competitorAlerts = useAppSelector((state) => state.competitor.allAlerts);
  const computeAndShowSummary = useCallback((sessionName: string = 'Session') => {
    // Compute duration
    const callDuration = sessionManager.getCallDuration();
    let durationStr = '0s';
    if (callDuration !== null) {
      const hours = Math.floor(callDuration / 3600);
      const minutes = Math.floor((callDuration % 3600) / 60);
      const seconds = callDuration % 60;
      if (hours > 0) {
        durationStr = `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        durationStr = `${minutes}m ${seconds}s`;
      } else {
        durationStr = `${seconds}s`;
      }
    }

    // Count by speaker
    const salespersonTranscripts = transcripts.filter(t => t.speaker === 'salesperson').length;
    const customerTranscripts = transcripts.filter(t => t.speaker === 'customer').length;
    const salespersonLandmines = landmineAlerts.filter(a => a.speaker?.toLowerCase() === 'salesperson').length;
    const customerLandmines = landmineAlerts.filter(a => a.speaker?.toLowerCase() !== 'salesperson').length;
    const salespersonCompetitors = competitorAlerts.filter(a => a.speaker?.toLowerCase() === 'salesperson').length;
    const customerCompetitors = competitorAlerts.filter(a => a.speaker?.toLowerCase() !== 'salesperson').length;

    // Count questions by category
    const objections = entries.filter(e => e.category?.toLowerCase() === 'objection').length;
    const faqs = entries.filter(e => e.category?.toLowerCase() === 'faq').length;
    const newInquiries = entries.filter(e => {
      const cat = e.category?.toLowerCase() || '';
      return cat !== 'objection' && cat !== 'faq';
    }).length;

    const summaryData: SessionSummaryData = {
      sessionName,
      duration: durationStr,
      totalTranscripts: transcripts.length,
      salespersonTranscripts,
      customerTranscripts,
      totalLandmines: landmineAlerts.length,
      salespersonLandmines,
      customerLandmines,
      totalCompetitors: competitorAlerts.length,
      salespersonCompetitors,
      customerCompetitors,
      objections,
      faqs,
      newInquiries,
    };

    setSessionSummaryData(summaryData);
    setShowSessionSummary(true);
  }, [transcripts, entries, landmineAlerts, competitorAlerts]);

  const closeSummary = useCallback(() => {
    setShowSessionSummary(false);
  }, []);

  const clearSessionData = useCallback(() => {
    dispatch(clearConversation());
    dispatch(clearLandmineAlerts());
    dispatch(clearCompetitorAlerts());
    dispatch(clearSalesPlaybookSession());
    setShowSessionSummary(false);
    setSessionSummaryData(null);
  }, [dispatch]);

  return {
    showSessionSummary,
    sessionSummaryData,
    computeAndShowSummary,
    closeSummary,
    clearSessionData,
  };
}
